/**
 * Job Progress Utilities
 *
 * Pure helpers for the job-progress foundation: the known-job registry,
 * channel naming, labelling (never the raw UUID), and payload extraction.
 */

import { JOB_PROGRESS_CONSTANTS } from '../config/constants';

import type {
  JobProgress,
  JobStatus,
  JobType,
} from '../types/job-progress.types';

/**
 * Known jobs that broadcast progress. `label` is the display fallback when the
 * broadcast omits `job_name`; `steps` is the expected total (useful before the
 * first event arrives). See docs/prd/prd-job-progress-tracking.md §4.
 */
export const JOB_REGISTRY: Record<JobType, { label: string; steps: number }> = {
  CopySharedWorkoutSessionData: {
    label: 'Copy Shared Workout Session',
    steps: 3,
  },
  ExportWorkoutHistory: { label: 'Workout History Export', steps: 4 },
  ExportChallengeReport: { label: 'Challenge Report Export', steps: 4 },
  ExportUserData: { label: 'Complete Data Export', steps: 4 },
  ExportProgressReport: { label: 'Progress Report Export', steps: 4 },
  GeneratePersonalizedPlan: { label: 'Personalized Plan Generation', steps: 2 },
};

const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>([
  'completed',
  'failed',
  'held',
]);

/**
 * Whether a status is terminal (`completed`, `failed` or `held`) — stop
 * tracking. `held` ends the job without a result and without an error: the
 * work waits on a person (a plan held for an admin's review).
 */
export function isTerminalJobStatus(status: JobStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** `metadata.reason` of a plan held because the answers declare an injury. */
const HELD_REASON_LIMITATIONS = 'user_limitations';

/**
 * Whether a job ended held for review rather than done or broken.
 *
 * The API says so with status `held`. Before that status existed it reported
 * the same outcome as `failed` with `metadata.reason: "user_limitations"`, and
 * an API that has not shipped the change still does — so that pair is read as
 * held too, and never shown as a failure.
 */
export function isHeldJob(
  progress: Pick<JobProgress, 'status' | 'metadata'>
): boolean {
  if (progress.status === 'held') return true;
  return (
    progress.status === 'failed' &&
    progress.metadata.reason === HELD_REASON_LIMITATIONS
  );
}

/** Whether a job really failed — a held job is not a failure. */
export function isFailedJob(
  progress: Pick<JobProgress, 'status' | 'metadata'>
): boolean {
  return progress.status === 'failed' && !isHeldJob(progress);
}

/** Whether a `job_type` string is one the client knows by name. */
export function isKnownJobType(jobType: string | null): jobType is JobType {
  return jobType !== null && jobType in JOB_REGISTRY;
}

/**
 * Whether a personalised plan finished generating between two snapshots of
 * the tracked jobs — a `GeneratePersonalizedPlan` job that is `completed` now
 * and was not before. The plan, its workouts and the dashboard's plan counts
 * all changed on the server when that happens, so caches of them are stale.
 */
export function didPlanGenerationComplete(
  jobs: Record<string, Pick<JobProgress, 'job_type' | 'status'>>,
  previousJobs: Record<string, Pick<JobProgress, 'job_type' | 'status'>>
): boolean {
  return Object.entries(jobs).some(
    ([jobId, job]) =>
      job.job_type === 'GeneratePersonalizedPlan' &&
      job.status === 'completed' &&
      previousJobs[jobId]?.status !== 'completed'
  );
}

/**
 * Echo channel name for a user's job-progress stream — WITHOUT the `private-`
 * prefix, which Echo's private-channel subscription adds itself.
 */
export function createUserJobsChannel(username: string): string {
  return JOB_PROGRESS_CONSTANTS.USER_JOBS_PRIVATE_CHANNEL.replace(
    'username',
    username
  );
}

/**
 * Display label for a job, never the raw UUID:
 * broadcast `job_name` → registry label → `job_type` → generic fallback.
 */
export function getJobLabel(
  progress: Pick<JobProgress, 'job_name' | 'job_type'>
): string {
  const name = progress.job_name?.trim();
  if (name) return name;
  if (isKnownJobType(progress.job_type)) {
    return JOB_REGISTRY[progress.job_type].label;
  }
  return progress.job_type ?? 'Background task';
}

/**
 * Pull a `JobProgress` out of an Echo broadcast payload. Tolerates both the
 * wrapped envelope (`{ job_id, progress }`) and a bare `JobProgress` (the REST
 * fallback shape). Returns null when the payload isn't a usable snapshot.
 */
export function extractJobProgressFromPayload(
  payload: unknown
): JobProgress | null {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const candidate =
    'progress' in record && record.progress ? record.progress : record;

  if (!candidate || typeof candidate !== 'object') return null;

  const snapshot = candidate as Record<string, unknown>;
  if (
    typeof snapshot.job_id !== 'string' ||
    typeof snapshot.status !== 'string'
  ) {
    return null;
  }

  return snapshot as unknown as JobProgress;
}
